import React, { useState, useEffect } from 'react';
import AdminPageTitle from '../components/AdminPageTitle';
import './Orders.css';
import { fetchAdminOrders, fetchAdminOrder, updateAdminOrderStatus, updateAdminOrder, downloadOrderPdf, downloadOrderExcel } from '../services/orders';
import { getStorageUrl } from '../services/api';
import { useAdminNotifications } from '../context/AdminNotificationsContext';

const Orders = () => {
  const { refetch: refetchNotifications } = useAdminNotifications();
  const [orders, setOrders] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [loading, setLoading] = useState(false);
  const [filterStatus, setFilterStatus] = useState('all');
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [error, setError] = useState(null);
  const [editMode, setEditMode] = useState(false);
  const [receiptModalOrder, setReceiptModalOrder] = useState(null);
  const [downloadingId, setDownloadingId] = useState(null); // id when PDF/Excel download in progress
  const itemsPerPage = 10;

  useEffect(() => {
    const loadOrders = async () => {
      try {
        setLoading(true);
        setError(null);
        const data = await fetchAdminOrders({
          page: currentPage,
          per_page: itemsPerPage,
          status: filterStatus !== 'all' ? filterStatus : undefined,
        });
        setOrders(data.data || []);
        setTotalPages(data.last_page || 1);
      } catch (err) {
        console.error('Error fetching orders:', err);
        setError(err.response?.data?.message || err.message || 'Failed to load orders.');
        setOrders([]);
        setTotalPages(1);
      } finally {
        setLoading(false);
      }
    };

    loadOrders();
  }, [currentPage, filterStatus]);

  const handlePageChange = (page) => {
    setCurrentPage(page);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleStatusChange = async (orderId, newStatus) => {
    try {
      await updateAdminOrderStatus(orderId, newStatus.toLowerCase());
      setOrders((prev) =>
        prev.map((order) =>
          order.id === orderId ? { ...order, status: newStatus.toLowerCase() } : order
        )
      );
      refetchNotifications();
    } catch (err) {
      console.error('Failed to update order status', err);
      alert(err.response?.data?.message || 'Failed to update order status');
    }
  };

  const getStatusClass = (status) => {
    const normalized = (status || '').toLowerCase();
    const statusMap = {
      pending: 'status-pending',
      processing: 'status-processing',
      purchased: 'status-purchased',
      shipped_out: 'status-shipped-out',
      in_nigeria: 'status-in-nigeria',
      completed: 'status-completed',
      refund: 'status-refund',
      cancelled: 'status-cancelled',
    };
    return statusMap[normalized] || 'status-default';
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
  };

  return (
    <div className="admin-orders-page">
      <AdminPageTitle title="Orders" />
      <div className="page-header">
        <h1>Orders Management</h1>
      </div>

      <div className="orders-filters">
        <div className="filter-group">
          <label>Filter by Status:</label>
          <select
            value={filterStatus}
            onChange={(e) => {
              setFilterStatus(e.target.value);
              setCurrentPage(1);
            }}
            className="filter-select"
          >
            <option value="all">All Orders</option>
            <option value="pending">Pending</option>
            <option value="processing">Processing</option>
            <option value="purchased">Purchased</option>
            <option value="shipped_out">Shipped Out</option>
            <option value="in_nigeria">In Nigeria</option>
            <option value="completed">Completed</option>
            <option value="refund">Refund</option>
            <option value="cancelled">Cancelled</option>
          </select>
        </div>
      </div>

      {loading ? (
        <div className="loading">Loading orders...</div>
      ) : (
        <>
          {error && (
            <div className="loading" style={{ color: '#c33' }}>
              {error}
            </div>
          )}
          <div className="table-container">
            <table className="data-table">
              <thead>
                <tr>
                  <th>Image</th>
                  <th>Order ID</th>
                  <th>Customer</th>
                  <th>Items</th>
                  <th>Total</th>
                  <th>Status</th>
                  <th>Date</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {orders.length === 0 ? (
                  <tr>
                    <td colSpan="8" className="no-data">No orders found</td>
                  </tr>
                ) : (
                  orders.map(order => {
                    const firstImg = order.items?.[0]?.product_image ?? order.items?.[0]?.product?.image ?? order.items?.[0]?.product_snapshot?.product?.image;
                    return (
                    <tr key={order.id}>
                      <td>
                        {firstImg ? (
                          <img src={getStorageUrl(firstImg)} alt="" className="order-row-img" />
                        ) : (
                          <span className="order-row-no-img">—</span>
                        )}
                      </td>
                      <td>{order.order_number || order.id}</td>
                      <td>
                        <div className="customer-info">
                          <div className="customer-name">{order.user?.email || 'N/A'}</div>
                          <div className="customer-details">{order.user?.phone || ''}</div>
                        </div>
                      </td>
                      <td>
                        <div className="order-items">
                          {order.items?.map((item, index) => (
                            <div key={index} className="order-item">
                              {item.quantity}x {item.product_name}
                              {item.selected_size && <span className="item-size"> ({item.selected_size})</span>}
                            </div>
                          ))}
                        </div>
                      </td>
                      <td>¥{Number(order.total || 0).toLocaleString()}</td>
                      <td>
                        <select
                          value={(order.status || '').toLowerCase()}
                          onChange={(e) => handleStatusChange(order.id, e.target.value)}
                          className={`status-select ${getStatusClass(order.status)}`}
                        >
                          <option value="pending">Pending</option>
                          <option value="processing">Processing</option>
                          <option value="purchased">Purchased</option>
                          <option value="shipped_out">Shipped Out</option>
                          <option value="in_nigeria">In Nigeria</option>
                          <option value="completed">Completed</option>
                          <option value="refund">Refund</option>
                          <option value="cancelled">Cancelled</option>
                        </select>
                      </td>
                      <td>{formatDate(order.created_at)}</td>
                      <td>
                        <button
                          className="action-btn view-btn"
                          onClick={async () => {
                            try {
                              setDetailLoading(true);
                              const fullOrder = await fetchAdminOrder(order.id);
                              setSelectedOrder(fullOrder);
                            } catch (err) {
                              console.error('Failed to load order details', err);
                              alert(err.response?.data?.message || 'Failed to load order details');
                            } finally {
                              setDetailLoading(false);
                            }
                          }}
                        >
                          {detailLoading && selectedOrder?.id === order.id ? 'Loading...' : 'View'}
                        </button>
                        <button
                          className="action-btn edit-btn"
                          onClick={async () => {
                            try {
                              setDetailLoading(true);
                              const fullOrder = await fetchAdminOrder(order.id);
                              setSelectedOrder(fullOrder);
                              setEditMode(true);
                            } catch (err) {
                              console.error('Failed to load order details', err);
                              alert(err.response?.data?.message || 'Failed to load order details');
                            } finally {
                              setDetailLoading(false);
                            }
                          }}
                        >
                          Edit
                        </button>
                        <button
                          type="button"
                          className="action-btn download-btn"
                          title="Download as PDF"
                          disabled={downloadingId === order.id}
                          onClick={async () => {
                            try {
                              setDownloadingId(order.id);
                              await downloadOrderPdf(order.id);
                            } catch (err) {
                              console.error('Failed to download PDF', err);
                              alert(err.message || 'Failed to download PDF');
                            } finally {
                              setDownloadingId(null);
                            }
                          }}
                        >
                          <span className="download-icon" aria-hidden>📄</span>
                          <span className="download-label">PDF</span>
                        </button>
                        <button
                          type="button"
                          className="action-btn download-btn excel-btn"
                          title="Download as Excel (CSV)"
                          disabled={downloadingId === order.id}
                          onClick={async () => {
                            try {
                              setDownloadingId(order.id);
                              await downloadOrderExcel(order.id);
                            } catch (err) {
                              console.error('Failed to download Excel', err);
                              alert(err.message || 'Failed to download Excel');
                            } finally {
                              setDownloadingId(null);
                            }
                          }}
                        >
                          <span className="download-icon" aria-hidden>📊</span>
                          <span className="download-label">Excel</span>
                        </button>
                        {order.payment_receipt && (
                          <button
                            type="button"
                            className="action-btn receipt-btn"
                            onClick={() => setReceiptModalOrder(order)}
                            title="View payment receipt"
                          >
                            📄
                          </button>
                        )}
                      </td>
                    </tr>
                  );
                  })
                )}
              </tbody>
            </table>
          </div>

          {totalPages > 1 && (
            <div className="pagination">
              <button
                className="pagination-btn"
                onClick={() => handlePageChange(currentPage - 1)}
                disabled={currentPage === 1}
              >
                Previous
              </button>
              
              <div className="pagination-numbers">
                {Array.from({ length: totalPages }, (_, i) => i + 1).map(page => (
                  <button
                    key={page}
                    className={`pagination-number ${currentPage === page ? 'active' : ''}`}
                    onClick={() => handlePageChange(page)}
                  >
                    {page}
                  </button>
                ))}
              </div>

              <button
                className="pagination-btn"
                onClick={() => handlePageChange(currentPage + 1)}
                disabled={currentPage === totalPages}
              >
                Next
              </button>
            </div>
          )}
        </>
      )}

      {receiptModalOrder && (
        <div className="order-modal-overlay" onClick={() => setReceiptModalOrder(null)}>
          <div className="receipt-modal" onClick={(e) => e.stopPropagation()}>
            <div className="order-modal-header">
              <h2>Payment receipt – {receiptModalOrder.order_number || receiptModalOrder.id}</h2>
              <button type="button" className="order-modal-close" onClick={() => setReceiptModalOrder(null)}>×</button>
            </div>
            <div className="receipt-modal-body">
              {receiptModalOrder.payment_receipt_note && (
                <p className="receipt-modal-note">{receiptModalOrder.payment_receipt_note}</p>
              )}
              <img src={getStorageUrl(receiptModalOrder.payment_receipt)} alt="Payment receipt" className="receipt-modal-img" />
            </div>
          </div>
        </div>
      )}

      {selectedOrder && (
        <div className="order-modal-overlay" onClick={() => { setSelectedOrder(null); setEditMode(false); }}>
          <div className="order-modal" onClick={(e) => e.stopPropagation()}>
            <div className="order-modal-header">
              <h2>Order {selectedOrder.order_number || selectedOrder.id}</h2>
              <button className="order-modal-close" onClick={() => { setSelectedOrder(null); setEditMode(false); }}>×</button>
            </div>
            <div className="order-modal-body">
              <div className="order-modal-section">
                <h3>Customer</h3>
                <p>Email: {selectedOrder.user?.email || 'N/A'}</p>
                <p>Phone: {selectedOrder.user?.phone || 'N/A'}</p>
              </div>
              <div className="order-modal-section">
                <h3>Order Info</h3>
                <p>Status: {(selectedOrder.status || '').toUpperCase()}</p>
                <p>Total: ¥{Number(selectedOrder.total || 0).toLocaleString()}</p>
                {selectedOrder.currency_rate != null && (
                  <p>Currency rate: ₦{Number(selectedOrder.currency_rate).toLocaleString()} per Yuan</p>
                )}
                <p>Placed: {formatDate(selectedOrder.created_at)}</p>
              </div>
              <div className="order-modal-section">
                <h3>Items</h3>
                <ul className="order-modal-items">
                  {selectedOrder.items?.map((item) => {
                    const snapshot = item.product_snapshot || {};
                    const variant = snapshot.variant || null;
                    const imgPath = item.product_image ?? item.product?.image ?? snapshot.product?.image;
                    return (
                      <li key={item.id} className="order-modal-item">
                        {imgPath && (
                          <img src={getStorageUrl(imgPath)} alt={item.product_name} className="order-modal-item-img" />
                        )}
                        <div>
                          <strong>{item.product_name}</strong>
                          {variant && (
                            <div className="order-modal-variant">
                              Variant: {variant.spec_name}: {variant.spec_value}
                            </div>
                          )}
                          {item.selected_size && !variant && (
                            <div className="order-modal-variant">
                              Specification: {item.selected_size}
                            </div>
                          )}
                          <div>Qty: {item.quantity}</div>
                          <div>
                            Price:{' '}
                            {editMode ? (
                              <input
                                type="number"
                                min="0"
                                step="0.01"
                                value={item.price}
                                onChange={(e) => {
                                  const value = parseFloat(e.target.value) || 0;
                                  setSelectedOrder((prev) => ({
                                    ...prev,
                                    items: prev.items.map((it) =>
                                      it.id === item.id
                                        ? { ...it, price: value, subtotal: value * it.quantity }
                                        : it
                                    ),
                                    subtotal: prev.items.reduce((sum, it) =>
                                      it.id === item.id
                                        ? sum + value * it.quantity
                                        : sum + it.subtotal
                                    , 0),
                                    total: prev.items.reduce((sum, it) =>
                                      it.id === item.id
                                        ? sum + value * it.quantity
                                        : sum + it.subtotal
                                    , 0),
                                  }));
                                }}
                                className="order-edit-input"
                              />
                            ) : (
                              <>¥{Number(item.price || 0).toLocaleString()}</>
                            )}
                          </div>
                          <div>Subtotal: ¥{Number(item.subtotal || 0).toLocaleString()}</div>
                        </div>
                      </li>
                    );
                  })}
                </ul>
              </div>
              {editMode && (
                <div className="order-modal-section">
                  <button
                    type="button"
                    className="order-save-btn"
                    onClick={async () => {
                      try {
                        const payload = {
                          items: selectedOrder.items.map((it) => ({
                            id: it.id,
                            price: Number(it.price || 0),
                          })),
                        };
                        const res = await updateAdminOrder(selectedOrder.id, payload);
                        setSelectedOrder(res.order);
                        setOrders((prev) =>
                          prev.map((o) =>
                            o.id === res.order.id ? { ...o, total: res.order.total } : o
                          )
                        );
                        setEditMode(false);
                      } catch (err) {
                        console.error('Failed to save order changes', err);
                        alert(err.response?.data?.message || 'Failed to save order changes');
                      }
                    }}
                  >
                    Save Changes
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Orders;
